// Classifies a thrown value by what the person can do about it, not by where it
// came from. Matches on shape rather than `instanceof` so it imports nothing:
// this is reached from the first surface that loads.

export type FailureKind = 'transient' | 'auth' | 'notFound' | 'offline' | 'bug';

//   request -> ask again; the same call may succeed
//   reload  -> the page must be rebuilt; asking again cannot help
//   none    -> nothing the person does changes the answer
export type Recovery = 'request' | 'reload' | 'none';

export interface Failure {
  kind: FailureKind;
  message: string;
  recovery: Recovery;
  // Shown only behind a disclosure.
  detail: string | null;
}

// What an error screen should offer. One action, never two: a retry beside a
// reload reads as a choice when it is not.
//
// `reloadIsDestructive` inverts the usual answer. A failure classed `reload` is
// one where asking again provably cannot help, so normally there is nothing to
// gain from a retry -- but where reloading would cost something the failure did
// not, a cheap action that might not work beats an expensive one that certainly
// does. Nothing is offered rather than a reload that could end a call.
export function chooseAction(
  recovery: Recovery,
  canRetry: boolean,
  reloadIsDestructive: boolean,
): 'retry' | 'reload' | null {
  if (recovery === 'none') return null;
  if (recovery === 'request') return canRetry ? 'retry' : null;
  if (!reloadIsDestructive) return 'reload';
  return canRetry ? 'retry' : null;
}

// Only a real HTTP code counts. A domain object carrying `status: 'pending'`
// shares the name, and reading it as a code would class a render bug as a 404.
function httpStatus(error: unknown): number | null {
  try {
    if (typeof error !== 'object' || error === null) return null;
    const status: unknown = (error as { status?: unknown }).status;
    if (typeof status !== 'number' || !Number.isInteger(status)) return null;
    return status >= 100 && status <= 599 ? status : null;
  } catch {
    return null;
  }
}

const MAX_DETAIL = 300;

function clip(value: string): string | null {
  const text = value.trim();
  if (!text) return null;
  return text.length > MAX_DETAIL ? `${text.slice(0, MAX_DETAIL)}…` : text;
}

// Guarded because `message` can be a getter that throws, and clipped because a
// rejected request carries the response body verbatim when it is not the shape
// expected -- a proxy's HTML error page reaches here whole. Not `String(error)`:
// that yields "[object Object]" for a thrown plain object.
export function detailOf(error: unknown): string | null {
  try {
    if (typeof error === 'string') return clip(error);
    if (typeof error === 'object' && error !== null) {
      const message: unknown = (error as { message?: unknown }).message;
      if (typeof message === 'string') return clip(message);
    }
  } catch {
    return null;
  }
  return null;
}

// A dynamic import that never arrived. No code is given and the wording varies
// by bundler and browser, so this matches all of the phrasings in use.
export function isChunkLoadError(error: unknown): boolean {
  const name =
    typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : null;
  if (name === 'ChunkLoadError') return true;
  const detail = detailOf(error)?.toLowerCase();
  if (detail == null) return false;
  return (
    detail.includes('dynamically imported module') ||
    detail.includes('loading chunk') ||
    detail.includes('importing a module script failed')
  );
}

// A request that failed at the network layer, before any status came back.
// Wording varies by browser and none of them give a code.
function isNetworkError(error: unknown): boolean {
  const detail = detailOf(error)?.toLowerCase();
  if (detail == null) return false;
  return (
    detail.includes('failed to fetch') ||
    detail.includes('networkerror') ||
    detail.includes('network request failed') ||
    detail.includes('load failed')
  );
}

// An unreadable `navigator` reads as online: a wrong "you are offline" sends
// someone to check their wifi while the fault is ours.
function isOffline(): boolean {
  return typeof navigator === 'object' && navigator !== null && navigator.onLine === false;
}

// Never throws -- this runs inside a boundary, where a throw means the fallback
// fails, renders again, and the page dies for good.
export function classify(error: unknown): Failure {
  const detail = detailOf(error);
  const status = httpStatus(error);

  if (status === 401 || status === 403) {
    return { kind: 'auth', message: 'You do not have access to this.', recovery: 'none', detail };
  }

  if (status === 404) {
    return { kind: 'notFound', message: 'This no longer exists.', recovery: 'none', detail };
  }

  if (status != null && status >= 500) {
    return {
      kind: 'transient',
      message: 'Something on our side did not answer.',
      recovery: 'request',
      detail,
    };
  }

  // Every other 4xx: the server understood and refused, so the same request
  // cannot change its mind -- but the page state that built it can be rebuilt.
  if (status != null && status >= 400) {
    return {
      kind: 'bug',
      message: 'This page asked for something it should not have.',
      recovery: 'reload',
      detail,
    };
  }

  if (isChunkLoadError(error)) {
    return {
      kind: 'transient',
      message: 'Part of the page could not be downloaded.',
      recovery: 'reload',
      detail,
    };
  }

  // Gated on the error looking like a failed request, not on `navigator.onLine`
  // alone. A render bug that happens to be thrown while the wifi is down is
  // still a render bug: blaming the network gives it the wrong message, offers
  // a retry against a tree that cannot render, and hides navigation that works.
  if (isNetworkError(error)) {
    return isOffline()
      ? { kind: 'offline', message: 'You appear to be offline.', recovery: 'request', detail }
      : {
          kind: 'transient',
          message: 'We could not reach the server.',
          recovery: 'request',
          detail,
        };
  }

  return { kind: 'bug', message: 'Something went wrong on this page.', recovery: 'reload', detail };
}
